import { CampingSpot } from '../types';
import { generateSpotName, generateSpotShareSummary } from './geminiService';
import { upsertSpotsToFirebase } from './firebaseService';
import { shouldAutoGenerateSpotDescription } from './spotIdentity';
import i18n from '../i18n';

const isMissingSpotTitle = (name?: string) => {
  if (!name || !name.trim()) return true;
  return /^(lokasjon|punkt|unnamed|unavngitt)$/i.test(name.trim());
};

const isMissingSpotDescription = (text?: string) => {
  if (!text || !text.trim()) return true;
  return /analyserer|analyzing|waiting for analysis|venter pa analyse|punkt funnet i kartdata|dette er en vill campingplass identifisert|beskrivelse kommer/i.test(text);
};

const buildFallbackDescription = (spot: CampingSpot) => {
  const road = spot.highway || i18n.t('share_fallback.unknown_road');
  const surface = (spot.surfaceType || i18n.t('share_fallback.unknown_surface')).toLowerCase();
  const nearestHouse = typeof spot.nearestHouse === 'number'
    ? i18n.t('share_fallback.distance_to_settlement', { meters: Math.round(spot.nearestHouse) })
    : i18n.t('share_fallback.unknown_distance');
  return i18n.t('share_fallback.description', { road, surface, distance: nearestHouse });
};

export const ensureSpotShareMetadata = async (spot: CampingSpot): Promise<CampingSpot | null> => {
  // Bom/forbudt-sone/attraksjon skal ikke få AI-generert campingbeskrivelse.
  if (!shouldAutoGenerateSpotDescription(spot)) return null;

  const missingTitle = isMissingSpotTitle(spot.name);
  const currentDescription = spot.aiResult?.description || spot.description || '';
  const missingDescription = isMissingSpotDescription(currentDescription);

  if (!missingTitle && !missingDescription) return null;

  const [title, summary] = await Promise.all([
    missingTitle
      ? generateSpotName({
          municipality: spot.property?.kommunenavn,
          county: spot.property?.fylkesnavn,
          highway: spot.highway,
          surfaceType: spot.surfaceType,
          features: spot.features
        })
      : Promise.resolve(null),
    missingDescription
      ? generateSpotShareSummary({
          spotName: spot.name,
          highway: spot.highway,
          surfaceType: spot.surfaceType,
          nearestHouse: spot.nearestHouse,
          score: spot.score,
          features: spot.features,
          municipality: spot.property?.kommunenavn,
          county: spot.property?.fylkesnavn
        })
      : Promise.resolve(null)
  ]);

  const patch: Partial<CampingSpot> = {};
  if (title && title.trim()) patch.name = title.trim();
  const finalSummary = (summary && summary.trim()) ? summary.trim() : (missingDescription ? buildFallbackDescription(spot) : '');
  if (finalSummary) {
    patch.description = finalSummary;
    // Auto-generert tekst MÅ flagges descriptionAuto: true, ellers ser
    // berikelsen (descriptionIsReplaceable) på den som eier-skrevet og freder
    // den for alltid — det er slik villmarksblurber ble låst på kommersielle
    // plasser. Se services/spotIdentity.ts + functions/campsiteEnrich.js.
    patch.descriptionAuto = true;
    patch.aiResult = {
      ...(spot.aiResult || {} as any),
      description: finalSummary
    } as any;
  }

  if (Object.keys(patch).length === 0) return null;

  const updatedSpot: CampingSpot = { ...spot, ...patch };
  await upsertSpotsToFirebase([updatedSpot]);
  return updatedSpot;
};
